import { Component, OnInit } from '@angular/core';
import { MenuItem, PrimeNGConfig } from 'primeng/api';

import { AuthService, ROLES } from '@app/core/auth';
import { RequestService } from './core/request';
import { translations } from './translations';
import { rbac } from './shared/rbac';
import { ApplicationsService, IApplication } from './core/applications';
import { TranslateService } from '@ngx-translate/core';
import { environment } from 'src/environments/environment';
import { Router } from '@angular/router';
import { UsersService } from './core/users/users.service';
import { APP_VERSION } from 'src/environments/version';
import { SingleTabService } from './core/auth/single-tab-service';
import { firstValueFrom } from 'rxjs';

@Component({
  selector: 'app-root',
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.css'],
})
export class AppComponent implements OnInit {
  public isLoggedIn: boolean;
  public profile: any;
  public menuItems: SiemensMenuItem[];
  public isFetching: boolean;
  private _applications: IApplication[];
  public menuVisible: boolean = true;
  public username: string = '';
  public centers: any[] = [];
  public selectedCenter: any = null;
  public legalNotice: string = environment.legalNotice;
  public showLegalNotice: boolean = false;

  public showCenterSelector: boolean = false;
  public blocked: boolean = false;

  version = APP_VERSION;

  constructor(
    private auth: AuthService,
    request: RequestService,
    private primengConfig: PrimeNGConfig,
    private _applicationService: ApplicationsService,
    private translate: TranslateService,
    private _router: Router,
    private _userService: UsersService,
    private singleTabService: SingleTabService
  ) {
    this.auth.block$.subscribe((value) => {
      this.blocked = value;
    });
    // this language will be used as a fallback when a translation isn't found in the current language
    translate.setDefaultLang('es');

    // the lang to use, if the lang isn't available, it will use the current loader to get them
    translate.use('es');
    this.menuItems = [];
    this.isFetching = request.isFetching$.value;
    request.isFetching$.subscribe((isFetching) => {
      this.isFetching = isFetching;
    });
  }

  ngOnInit() {
    this._init();
  }

  public async _init() {
    this.setTranslations();
    if (await this.auth.isLoggedIn()) {
      await this.loadLoggedMenu();
      this.username = this.auth.getUsername().userName;
      // Solo ir a home si se abrió la raíz; no pisar rutas directas (acumulados, view-request, epidemiology-view, etc.)
      if (environment.home) {
        const path = this._router.url.split('?')[0].replace(/\/$/, '') || '/';
        if (path === '' || path === '/') {
          this._router.navigate(environment.home.split('/'));
        }
      }

      if (this.auth.hasProfile(rbac.profiles.WEBRESULTS_USER)) {
        this.showCenterSelector = true;
        this.getCenters();
      }
    } else {
      this.auth.login();
    }
  }

  get hasUserGuide(): boolean {
    return !!(environment.userGuideUrls || environment.userGuideUrl);
  }

  downloadUserGuide(): void {
    let pdfUrl: string | undefined;

    if (environment.userGuideUrls) {
      // Usar userGuideUrls si está disponible (nuevo formato por idioma)
      const currentLang = this.translate.currentLang || this.translate.defaultLang || 'es';
      pdfUrl = environment.userGuideUrls[currentLang];

      // Si no existe PDF para el idioma actual, usar el primero disponible o el primero del objeto
      if (!pdfUrl) {
        const availableLangs = Object.keys(environment.userGuideUrls);
        if (availableLangs.length > 0) {
          pdfUrl = environment.userGuideUrls[availableLangs[0]];
        }
      }
    } else if (environment.userGuideUrl) {
      // Retrocompatibilidad: usar userGuideUrl si existe
      pdfUrl = environment.userGuideUrl;
    }

    if (pdfUrl) {
      window.open(pdfUrl, '_blank');
    } else {
      console.warn(
        'User guide URL is not defined in the environment configuration.'
      );
    }
  }

  get clientLogoUrl(): string {
    return environment.clientLogoUrl || null;
  }

  toggleMenu() {
    this.menuVisible = !this.menuVisible;
  }

  showMenu() {
    if (environment.hideMenu) return false;
    // Pantalla Tabla de acumulados (1.2 / 2.3), Gráficas de evolución (1.3 / 2.4) y Ver petición (1.4): se abren en nueva pestaña sin menú
    if (
      this._router.url.includes('acumulados-view') ||
      this._router.url.includes('evolution-charts') ||
      this._router.url.includes('view-request') ||
      this._router.url.includes('epidemiology-view') ||
      this._router.url.includes('sensitivity-atb-view')
    ) {
      return false;
    }
    return true;
  }

  /** WIR-EF-03.02.5 / issue #521: pestaña acumulados (y afines) sin navegación desde logos/iconos a inicio. */
  get minimalStandaloneShell(): boolean {
    const path = this._router.url.split('?')[0];
    return (
      path.includes('acumulados-view') ||
      path.includes('evolution-charts') ||
      path.includes('view-request') ||
      path.includes('epidemiology-view') ||
      path.includes('sensitivity-atb-view')
    );
  }

  get readonlyHeaderCenterName(): string {
    const centerId = this.routeCentroId ?? this.selectedCenter;
    if (centerId == null || Number.isNaN(Number(centerId))) {
      return '';
    }
    const center = this.centers?.find((c) => Number(c?.centroId) === Number(centerId));
    return center?.centroName ?? String(centerId);
  }

  get showReadonlyHeaderCenter(): boolean {
    return (
      this.showCenterSelector &&
      (this.minimalStandaloneShell || !this.hasMultipleCenters) &&
      !!this.readonlyHeaderCenterName
    );
  }

  get hasMultipleCenters(): boolean {
    return (this.centers?.length ?? 0) > 1;
  }

  get showEditableHeaderCenter(): boolean {
    return this.showCenterSelector && !this.minimalStandaloneShell && this.hasMultipleCenters;
  }

  private get routeCentroId(): number | null {
    const query = this._router.url.split('?')[1]?.split('#')[0] ?? '';
    const raw = new URLSearchParams(query).get('centroId');
    if (raw == null || raw === '') {
      return null;
    }
    const n = Number(raw);
    return Number.isNaN(n) ? null : n;
  }

  /** Logos o icono usuario: a inicio salvo en pestañas sin navegación (issue #521). */
  goToHomeUnlessStandalone(): void {
    if (this.minimalStandaloneShell) {
      return;
    }
    this.goToHome();
  }

  async getAllowedMenuItems(
    items: SiemensMenuItem[]
  ): Promise<SiemensMenuItem[]> {
    return items.filter((item) => {
      if (item.role && !item.application) {
        return this.auth.isRole(item.role);
      }
      if (item.role && item.application) {
        const appAuthorized = this._applications.some(
          (application) => application.code === item.application
        );
        const roleAuthorized = this.auth.isRole(item.role);
        return appAuthorized && roleAuthorized;
      }
      if (item.application) {
        // check if the application is active, i.e the application is in the list of applications (_applications)
        return this._applications.some(
          (application) => application.code === item.application
        );
      }
      if (item.profiles) {
        return item.profiles.some((profile) => this.auth.hasProfile(profile));
      }
      return true;
    });
  }

  public getCenters(): void {
    if (this.auth.hasProfile(rbac.profiles.WEBRESULTS_USER)) {
      this._userService.getSisifoUserData().subscribe((userData) => {
        if (!userData) {
          return;
        }
        this.centers = userData.centers ?? [];
        this.selectedCenter = this.centers.find(
          (center) => center.centroDefault
        )?.centroId ?? this.centers[0]?.centroId ?? null;
        if (this.selectedCenter != null) {
          this._userService.setSisifoDefaultCenter(this.selectedCenter);
        }
      });
    }
  }

  onCenterChange($event: any): void {
    this._userService.setSisifoDefaultCenter(this.selectedCenter);
  }

  private async loadLoggedMenu(): Promise<void> {
    // load configured applications
    this._applications = await firstValueFrom(
      this._applicationService.getApplications()
    );
    const _defaultItems = [
      {
        label: this.translate.instant('MENU.APPLICATIONS'),
        routerLink: '/applications',
        role: ROLES.Admin,
      },
      {
        label: this.translate.instant('MENU.REPORTS'),
        routerLink: '/reports',
        role: ROLES.Admin,
      },
      {
        label: this.translate.instant('MENU.PANELS'),
        routerLink: '/panels',
        role: ROLES.Admin,
      },
      {
        label: this.translate.instant('MENU.PROFILES'),
        routerLink: '/profiles',
        role: ROLES.Admin,
      },
      {
        label: this.translate.instant('MENU.TRANSLATIONS'),
        routerLink: '/translations',
        role: ROLES.Admin,
      },
      {
        label: this.translate.instant('MENU.LOGS'),
        routerLink: '/logs',
        role: ROLES.Admin,
      },
      {
        label: this.translate.instant('MENU.USERS'),
        routerLink: '/users',
        role: ROLES.Admin,
      },
      {
        label: this.translate.instant('MENU.CENTERS'),
        routerLink: '/patient-requests/centers',
        role: ROLES.Admin,
        application: rbac.appllications.WEB_RESULTS,
      },
      {
        label: this.translate.instant('MENU.COMMENTS'),
        routerLink: '/multicast/comments',
        application: rbac.appllications.MULTICAST,
      },
      {
        separator: true,
        role: ROLES.Admin,
      },
      {
        label: this.translate.instant('MENU.MY_QUERIES'),
        routerLink: '/query',
        profiles: [
          rbac.profiles.QUERY_EXPORT_CRUD_GENERAL,
          rbac.profiles.QUERY_EXPORT_CRUD_MICRO,
          rbac.profiles.QUERY_READ_OWN,
        ],
      },
      {
        label: this.translate.instant('MENU.EXPORTS'),
        routerLink: '/exports',
        profiles: [rbac.profiles.QUERY_EXPORT_CRUD_GENERAL, rbac.profiles.QUERY_EXPORT_CRUD_MICRO],
      },
      {
        separator: true,
        profiles: [rbac.profiles.QUERY_EXPORT_CRUD_GENERAL, rbac.profiles.QUERY_EXPORT_CRUD_MICRO, rbac.profiles.QUERY_READ_OWN],
      },
      {
        label: this.translate.instant('WXRL.LIST_RULES'),
        routerLink: '/wxrl/rules',
        profiles: [
          rbac.profiles.WRULES_READER,
          rbac.profiles.WRULES_USER,
          rbac.profiles.WRULES_ADMIN,
        ],
      },
      {
        label: this.translate.instant('WXRL.LOGS'),
        routerLink: '/wxrl/logs',
        profiles: [
          rbac.profiles.WRULES_READER,
          rbac.profiles.WRULES_USER,
          rbac.profiles.WRULES_ADMIN,
        ],
      },
      {
        separator: true,
        profiles: [
          rbac.profiles.WRULES_READER,
          rbac.profiles.WRULES_USER,
          rbac.profiles.WRULES_ADMIN,
        ],
      },
      {
        label: this.translate.instant('MENU.MY_PANELS'),
        routerLink: '/my-panels',
        application: rbac.appllications.MULTICAST,
      },
      {
        separator: true,
        profiles: [rbac.profiles.WEBRESULTS_USER],
      },
      {
        label: this.translate.instant('MENU.LAB_RESULTS'),
        routerLink: '/patient-requests',
        profiles: [rbac.profiles.WEBRESULTS_USER],
      },
    ];
    this.menuItems = await this.getAllowedMenuItems(_defaultItems);
  }

  public login(): void {
    this.auth.login();
  }

  private setTranslations(): void {
    this.primengConfig.setTranslation(translations);
  }

  public logout(): void {
    this.auth.logout();
  }

  public goToHome(): void {
    const home = environment.home?.split('/') || [''];
    this._router.navigate(home);
  }
}

// Definir la interfaz SiemensMenuItem que hereda de MenuItem
export interface SiemensMenuItem extends MenuItem {
  role?: string;
  application?: string;
  profiles?: string[];
}
